import { SelectedFilter, ShopProductCategoryResponse } from 'src/Libs/shop/models/shop-product-category.model'
import {
  fetchCrossSellingProducts,
  fetchHomePage,
  fetchShopBanners,
  fetchShopCategory,
  fetchShopLinks,
  fetchSingleProduct,
  fetchOrderHistory,
  fetchOrderHistoryDetails,
  cancelShopProductOrder,
  getShopSearchAllProducts,
  getShopSearchSuggestions,
  fetchShopAddditionalProducts,
} from '../api/shop.api'
import {
  CrossSellingProductsResponse,
  ShopBannerResponse,
  ShopHomePageResponse,
  OrderHistoryResponse,
  OrderHistoryDetailResponse,
  CancelOrderShopResponse,
  SearchResponseModel,
  ShopAdditionalProductsModelResponse,
} from '../models'

import { useShopFetchWithResult } from './use-shop-fetch'
import { ProductResponse } from '../models/product.model'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { ApiResult } from 'src/Libs/api'

/*
 * This hook , holds all the general/common endpoints call
 */

interface UseShopHook {
  getHomePageData: () => Promise<ShopHomePageResponse[]>
  getShopLinks: () => Promise<ShopHomePageResponse[]>
  getShopCategory: (
    categoryId: string,
    page: number,
    sorting?: string,
    limit?: number,
    filters?: SelectedFilter[]
  ) => Promise<ShopProductCategoryResponse | null>
  getShopProduct: (productId: string) => Promise<ProductResponse | null>
  getCrossSellingProducts: (productId: string) => Promise<CrossSellingProductsResponse[] | null>
  getShopBanners: () => Promise<ShopBannerResponse | null>
  getOrderHistory: (page: number, limit: number, filterStatus: string[], filterSapOrderId: number | null) => Promise<OrderHistoryResponse | null>
  getOrderHistoryDetails: (sapOrderId: number) => Promise<OrderHistoryDetailResponse | null>
  cancelProductOrder: (sapOrderId: number) => Promise<ApiResult<CancelOrderShopResponse | AuthFetchError | Error>>
  getSearchAllProducts: (search: string, page: number, sorting?: string, filters?: SelectedFilter[]) => Promise<ShopProductCategoryResponse | null>
  getSearchSuggestions: (search: string) => Promise<SearchResponseModel | null>
  getShopAdditionalProducts: (mainProductId: string) => Promise<ShopAdditionalProductsModelResponse | null>
}

const useShop = (): UseShopHook => {
  const shopFetch = useShopFetchWithResult()

  const getHomePageData = async (): Promise<ShopHomePageResponse[]> => {
    return (await fetchHomePage(shopFetch)).value ?? []
  }

  const getShopLinks = async (): Promise<ShopHomePageResponse[]> => {
    return (await fetchShopLinks(shopFetch)).value ?? []
  }

  const getShopCategory = async (
    categoryId: string,
    page: number,
    sorting?: string,
    limit?: number,
    filters?: SelectedFilter[]
  ): Promise<ShopProductCategoryResponse | null> => {
    return (await fetchShopCategory(categoryId, page, shopFetch, sorting, limit, filters)).value ?? null
  }

  const getShopProduct = async (productId: string): Promise<ProductResponse | null> => {
    return (await fetchSingleProduct(productId, shopFetch)).value ?? null
  }
  const getCrossSellingProducts = async (productId: string): Promise<CrossSellingProductsResponse[] | null> => {
    return (await fetchCrossSellingProducts(productId, shopFetch)).value ?? null
  }
  const getShopBanners = async (): Promise<ShopBannerResponse | null> => {
    return (await fetchShopBanners(shopFetch)).value ?? null
  }
  const getOrderHistory = async (
    page: number,
    limit: number,
    filterStatus: string[],
    filterSapOrderId: number | null
  ): Promise<OrderHistoryResponse | null> => {
    const response = await fetchOrderHistory(page, limit, filterStatus, filterSapOrderId, shopFetch)
    return response.value
  }
  const getOrderHistoryDetails = async (sapOrderId: number): Promise<OrderHistoryDetailResponse | null> => {
    const response = await fetchOrderHistoryDetails(sapOrderId, shopFetch)
    return response.value
  }
  const cancelProductOrder = async (sapOrderId: number): Promise<ApiResult<CancelOrderShopResponse | AuthFetchError | Error>> => {
    const response = await cancelShopProductOrder(sapOrderId, shopFetch)
    return response
  }
  const getSearchAllProducts = async (
    search: string,
    page: number,
    sorting?: string,
    filters?: SelectedFilter[]
  ): Promise<ShopProductCategoryResponse | null> => {
    try {
      const response = await getShopSearchAllProducts(search, page, shopFetch, sorting, filters)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    }
  }
  const getSearchSuggestions = async (search: string): Promise<SearchResponseModel | null> => {
    try {
      const response = await getShopSearchSuggestions(search, shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    }
  }
  const getShopAdditionalProducts = async (mainProductId: string): Promise<ShopAdditionalProductsModelResponse | null> => {
    try {
      const response = await fetchShopAddditionalProducts(mainProductId, shopFetch)
      return response.value
    } catch (error) {
      console.error(error)
      return null
    }
  }

  return {
    getHomePageData,
    getShopLinks,
    getShopCategory,
    getShopProduct,
    getCrossSellingProducts,
    getOrderHistory,
    getShopBanners,
    getOrderHistoryDetails,
    cancelProductOrder,
    getSearchAllProducts,
    getSearchSuggestions,
    getShopAdditionalProducts,
  }
}

export default useShop
